import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ERoutes } from '../../../../routes';
import { ApplicationState } from '../../../../store';
import { updateLongSteelShoppingCartSettings } from '../../../../store/ducks/long-steel-shopping-cart/actions';
import { ILongSteelOrderItem } from '../../../../store/ducks/long-steel-shopping-cart/types';
import { EPurchaseRoutes } from '../../purchase.routes';
import OrderSettingsConfig from './OrderSettingsConfig';
import OrderSettingsRoutes, {
  EOrderSettingsRoutes
} from './orderSettings.routes';
import OrderSettingsBottomBar from './OrderSettingsBottomBar';
import SearchedList from './SearchedList';
import { Container, TextBox } from './styles';
import {
  removeSearchedItem as removeSearchedItemAction,
  addSearchedItem as addSearchItemAction
} from '../../../../store/ducks/long-steel-order-item-search/actions';
import moment from 'moment';
import { getTotalQuantity } from '../../../../utils/purchaseHelpers';
import {
  isOutOfMinimunDate,
  isWeekend
} from './ShoppingCartTable/shoppingCartFieldsValidation';
import FooterButtonContainer from './SearchItem/FooterButtonContainer';
import { Link } from '@gerdau/hefesto/Link';
import { sendTagging } from '../../../../store/ducks/tagging/actions';

const OrderSettings: React.FC = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const orderItemSearch = useSelector(
    (state: ApplicationState) => state.longSteelOrderItemSearch
  );

  const [totalQuantity, setTotalQuantity] = useState(
    getTotalQuantity(shoppingCartState.items)
  );

  const [canShowSpreadsheetTemplate, setCanShowSpreadsheetTemplate] =
    useState(false);

  const taggingInfoPreviousScreen = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const addSearchedItensToCart = () => {
    const updatedItems = [
      ...shoppingCartState.items,
      ...orderItemSearch.searchedItems.filter(
        (item) => item.found && item.addedToCart
      )
    ];
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        items: updatedItems
      })
    );

    dispatch(removeSearchedItemAction([]));
    history.push(
      `${ERoutes.PURCHASE}/${EPurchaseRoutes.LONG_STEEL}/${EOrderSettingsRoutes.SHOPPING_CART}`
    );
  };

  const canShowOrderSettingsConfig =
    !pathname.includes(EOrderSettingsRoutes.CHECKOUT) &&
    !pathname.includes(EOrderSettingsRoutes.ENTRY_TYPE);

  const canShowSearchedItems = (): boolean => {
    return (
      (pathname.includes(EOrderSettingsRoutes.SEARCH_ITEMS) ||
        pathname.includes(EOrderSettingsRoutes.SPREADSHEET_UPLOAD)) &&
      orderItemSearch.searchedItems.length > 0
    );
  };

  const canShowSearchedItemsFloatBar = (): boolean => {
    if (pathname.includes(EOrderSettingsRoutes.REPEAT_ORDER)) {
      return true;
    }

    if (
      (pathname.includes(EOrderSettingsRoutes.SEARCH_ITEMS) ||
        pathname.includes(EOrderSettingsRoutes.SPREADSHEET_UPLOAD)) &&
      orderItemSearch.searchedItems.length > 0
    ) {
      return true;
    }

    return false;
  };

  const canShowShoppingCartFloatBar = (): boolean => {
    return (
      pathname.includes(EOrderSettingsRoutes.SHOPPING_CART) &&
      orderItemSearch.searchedItems.length < 1 &&
      shoppingCartState.items.length > 0
    );
  };

  const dispatchTaggingExclusionItem = (
    removedItems: ILongSteelOrderItem[]
  ): void => {
    const updatedItems = removedItems.map((remove: ILongSteelOrderItem) =>
      remove.sku ? remove.sku : remove.customerSku
    );
    const removeProducts = shoppingCartState.items.filter(
      (remove) =>
        !updatedItems.includes(remove.sku ? remove.sku : remove.customerSku)
    );
    const [removeProduct] = removeProducts;

    if (!removeProduct) {
      return;
    }

    dispatch(
      sendTagging({
        screenName: `LG:Comprar:AcosLongos:${taggingInfoPreviousScreen.screenName}:Detalhamento`,
        event: 'click',
        filterInfo: `#codEmr:${
          shoppingCartState.emitterId ? shoppingCartState.emitterId : 'not_set'
        }#codRbd:${
          shoppingCartState.receiverId
            ? shoppingCartState.receiverId
            : 'not_set'
        }`,
        itemDescription: `comprar_acosLongos_${taggingInfoPreviousScreen.itemDescription}_detalhamento_exclusaoProduto`,
        itemName: 'button:exclusao_produto',
        product: `${
          removeProducts.length
            ? removeProducts.map((item: { sku: string; customerSku: string }) =>
                item.sku ? item.sku : item.customerSku
              )
            : 'not_set'
        }`,
        orderCliente: shoppingCartState.customerOrder
          ? shoppingCartState.customerOrder
          : 'not_set'
      })
    );
  };

  const onRemoveItem = (idxsToRemove: number[]) => {
    const updatedItems = shoppingCartState.items.filter(
      (_, index) => !idxsToRemove.includes(index)
    );
    dispatchTaggingExclusionItem(updatedItems);
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        items: updatedItems
      })
    );
    setTotalQuantity(getTotalQuantity(updatedItems));
  };

  const addSearchedItem = (items: ILongSteelOrderItem[]) => {
    const updatedSearchedItems = orderItemSearch.searchedItems.map(
      (searchItem: ILongSteelOrderItem, index: number) => {
        const findSelectedItem = items.find(
          (addItem: ILongSteelOrderItem) => addItem.tableId === index
        );
        return findSelectedItem
          ? { ...searchItem, addedToCart: true }
          : searchItem;
      }
    );
    dispatch(addSearchItemAction([...updatedSearchedItems]));
  };

  const removeSearchedItem = (idx: number) => {
    const searchedItems = orderItemSearch.searchedItems;
    dispatch(
      removeSearchedItemAction([
        ...searchedItems.filter((_, index) => index !== idx)
      ])
    );
  };

  const onUpdateCartItem = () => {
    setTotalQuantity(getTotalQuantity(shoppingCartState.items));
    setCanGoToConfirmOrder(getCanGoToConfirmOrder());
  };

  const getCanGoToConfirmOrder = (): boolean => {
    return (
      shoppingCartState.receiverId !== '' &&
      !customersState.loading &&
      customersState.loadingPurchaseReceivers === false &&
      customersState.loadingProductDescription === false &&
      customersState.loadingBlocks === false &&
      !shoppingCartState.items.some(
        (item) =>
          !item.itemSaleInfo?.unityMeasure ||
          !isAvalidQuantity(item) ||
          !isAvalidDate(item) ||
          !item.itemSaleInfo.term ||
          (item.itemSaleInfo.customerPurchaseItem &&
            item.itemSaleInfo.customerPurchaseItem.length > 6)
      )
    );
  };

  const isAvalidQuantity = (item: ILongSteelOrderItem): boolean => {
    const value = item.itemSaleInfo.quantity;
    const measureUnit = item.destinations[0].measureUnits.find(
      (unit) => unit.measure === item.itemSaleInfo.unityMeasure
    );
    return (
      value !== undefined &&
      Number(value) !== 0 &&
      measureUnit !== undefined &&
      parseFloat(measureUnit.minimumQuantity) <= parseFloat(value) &&
      Math.round(parseFloat(value) * 1000) %
        Math.round(parseFloat(measureUnit.multiple) * 1000) ===
        0
    );
  };

  const isAvalidDate = (item: ILongSteelOrderItem): boolean => {
    const value = item.itemSaleInfo.requiredDate;
    return (
      !isOutOfMinimunDate(item.destinations[0].minimumDate, value) &&
      !isWeekend(moment(value, 'YYYY-MM-DD'))
    );
  };

  const [canGoToConfirmOrder, setCanGoToConfirmOrder] = useState(
    getCanGoToConfirmOrder()
  );

  const onConfirmClick = () => {
    history.push(
      `${ERoutes.PURCHASE}/${EPurchaseRoutes.LONG_STEEL}/${EOrderSettingsRoutes.CHECKOUT}`
    );
  };

  useEffect(() => {
    if (shoppingCartState.items.length > 0) {
      // atualizar o totalizador, quando adicionar item via planiha que tenha algum valor
      onUpdateCartItem();
    }
  });

  useEffect(() => {
    if (pathname) {
      setCanShowSpreadsheetTemplate(
        pathname.includes(EOrderSettingsRoutes.SPREADSHEET_UPLOAD)
      );
    }
  }, [pathname]);

  return (
    <Container>
      {canShowSpreadsheetTemplate && (
        <TextBox>
          <span>
            Adicione uma planilha de excel contendo o código dos SKUs, unidade
            de medida, quantidade e data desejada, e faremos a leitura do seu
            pedido automaticamente.
          </span>
          <span>
            Precisa de ajuda?{' '}
            <Link
              url={'/examples/Planilha_padrao_de_pedido.xlsx'}
              name={'Baixe aqui nosso modelo de planilha de pedidos.'}
            />
          </span>
        </TextBox>
      )}
      {canShowOrderSettingsConfig && (
        <OrderSettingsConfig
          emitterDisabled={
            shoppingCartState.items.length > 0 || orderItemSearch.searchingItems
          }
        />
      )}

      <>
        <OrderSettingsRoutes
          items={shoppingCartState.items}
          numberMaxOfItens={30}
          onUpdateCartItem={onUpdateCartItem}
          onRemoveItem={(idxsToRemove) => onRemoveItem(idxsToRemove)}
        />

        {canShowSearchedItems() && (
          <SearchedList
            searchedItems={orderItemSearch.searchedItems}
            foundItens={
              orderItemSearch.searchedItems.filter((item) => item.found).length
            }
            notFoundItens={
              orderItemSearch.searchedItems.filter((item) => !item.found).length
            }
            onRemoveSearchedItem={(idx: number) => removeSearchedItem(idx)}
            onAddSearchedItem={(items: ILongSteelOrderItem[]) =>
              addSearchedItem(items)
            }
          />
        )}
        {canShowSearchedItemsFloatBar() && (
          <FooterButtonContainer
            itensQuantity={
              orderItemSearch.searchedItems.filter((item) => item.found).length
            }
            notFoundItems={
              orderItemSearch.searchedItems.filter((item) => !item.found).length
            }
            onConfirmClick={() => addSearchedItensToCart()}
            disabledConfirmButton={
              !orderItemSearch.searchedItems.filter((item) => item.addedToCart)
                .length
            }
          ></FooterButtonContainer>
        )}
      </>

      {canShowShoppingCartFloatBar() && (
        <OrderSettingsBottomBar
          itensQuantity={shoppingCartState.items.length}
          totalQuantity={totalQuantity}
          onConfirmClick={onConfirmClick}
          disabledConfirmButton={!canGoToConfirmOrder}
          isShoppingCartFloatBar={true}
          numberMaxOfItens={30}
        />
      )}
    </Container>
  );
};

export default OrderSettings;
